import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import { getUserChatMessages, getUserById } from '../../services/users/usersService';
import {
  UserChatMessageDto,
  UserDto,
  // PaginatedChatMessageResponseDto, // Não está sendo usado
  ChatContent,
  ChatToolExecution
} from '../../services/users/types';
// import { MessageType } from '../../services/completions/types'; // Não está sendo usado
import ReactMarkdown from 'react-markdown';

// Interface já contém todos os campos necessários conforme o Swagger,
// mas mantemos para compatibilidade com o código existente
interface ExtendedUserChatMessageDto extends UserChatMessageDto {}

const UserChatHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDto | null>(null);
  const [messages, setMessages] = useState<ExtendedUserChatMessageDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pageSize = 20;

  // Fetch user data and initial messages
  useEffect(() => {
    if (id) {
      fetchUserData(id);
      fetchMessages(id, 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Scroll to bottom on initial load and after new messages are loaded
  useEffect(() => {
    if (!isLoading && !isLoadingMore && messages.length > 0) {
      // Sempre rolamos para o final quando as mensagens são carregadas inicialmente
      scrollToBottom();
    }
  }, [isLoading, isLoadingMore, messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Para scroll suave
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    } else {
      // Fallback para o método antigo
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const fetchUserData = async (userId: string) => {
    try {
      const userData = await getUserById(auth.user || null, userId);
      setUser(userData);
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  const fetchMessages = async (userId: string, page: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getUserChatMessages(auth.user || null, userId, page, pageSize);
      
      // A API retorna mensagens com as mais recentes primeiro,
      // mas queremos exibir em ordem cronológica (mais antigas primeiro)
      const orderedMessages = [...response.content].reverse();
      
      if (page === 1) {
        // Initial load
        setMessages(orderedMessages);
      } else {
        // Load more (adicionar mais mensagens antigas no início)
        setMessages(prev => [...orderedMessages, ...prev]);

        // Restaurar a posição de scroll após carregar mais mensagens
        setTimeout(() => {
          const savedPosition = (window as any).__scrollPosition;
          if (savedPosition) {
            const messagesContainer = messagesContainerRef.current;
            if (messagesContainer) {
              // Calcular a diferença de altura após adicionar novas mensagens
              const newScrollHeight = messagesContainer.scrollHeight;
              const heightDifference = newScrollHeight - savedPosition.height;

              // Ajustar o scroll para manter a mesma posição relativa
              messagesContainer.scrollTop = savedPosition.top + heightDifference;

              console.log(`Posição restaurada: nova altura=${newScrollHeight}, diferença=${heightDifference}, nova posição=${savedPosition.top + heightDifference}`);
            }
          }

          // Limpar dados salvos
          delete (window as any).__scrollPosition;
        }, 100); // Timeout para garantir que o DOM foi atualizado
      }

      setHasMore(
        response.content.length === pageSize &&
        page * pageSize < response.total_elements
      );
    } catch (err) {
      setError('Falha ao carregar mensagens. Por favor, tente novamente.');
      console.error('Erro ao buscar mensagens de chat:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      // Capture a referência do container de mensagens usando a ref
      const messagesContainer = messagesContainerRef.current;

      // Salvar a posição atual de scroll e altura do conteúdo
      if (messagesContainer) {
        const scrollHeight = messagesContainer.scrollHeight;
        const scrollTop = messagesContainer.scrollTop;

        // Armazenamos estas informações para uso após o carregamento
        (window as any).__scrollPosition = {
          height: scrollHeight,
          top: scrollTop,
          messageCount: messages.length
        };

        console.log(`Salvando posição: scrollHeight=${scrollHeight}, scrollTop=${scrollTop}`);
      }

      // Iniciar carregamento
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchMessages(id || '', nextPage);
    }
  };

  // Format message content based on its structure
  const formatMessageContent = (message: ExtendedUserChatMessageDto): string => {
    if (!message) return 'No content';

    // If content is a string (new API format), just return it
    if (typeof message.content === 'string') return message.content;

    // If content is an object with content property (legacy format)
    if (message.content && typeof message.content === 'object' &&
        (message.content as ChatContent).content) {
      return (message.content as ChatContent).content || '';
    }

    // Handle other possible content formats
    if (message.content && typeof message.content === 'object') {
      try {
        return JSON.stringify(message.content);
      } catch (e) {
        return 'Message content unavailable';
      }
    }

    // Default case
    return 'Message content unavailable';
  };

  // Helper for getting tool executions from different message formats
  const getToolExecutions = (message: ExtendedUserChatMessageDto): ChatToolExecution[] => {
    // Check for direct tool_executions property (new API format)
    if (message.tool_executions && Array.isArray(message.tool_executions)) {
      return message.tool_executions;
    }

    // Check for sources directly on the message (new API format)
    if (message.sources && Array.isArray(message.sources)) {
      // The sources might be related to tool executions in some contexts
      // This depends on your application's specific needs
    }

    // Check for legacy format with toolExecutions in the ChatContent object
    if (message.content && typeof message.content === 'object' &&
        (message.content as ChatContent).toolExecutions) {
      return (message.content as ChatContent).toolExecutions || [];
    }

    return [];
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString as string);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };
  
  // Group messages by date for better visual organization
  const groupMessagesByDate = () => {
    const groups: { date: string; messages: ExtendedUserChatMessageDto[] }[] = [];
    
    messages.forEach(message => {
      if (!message.created_at) return;
      
      const date = new Date(message.created_at);
      const dateString = date.toDateString();
      
      const existingGroup = groups.find(g => g.date === dateString);
      if (existingGroup) {
        existingGroup.messages.push(message);
      } else {
        groups.push({
          date: dateString,
          messages: [message]
        });
      }
    });
    
    return groups;
  };

  const messageGroups = groupMessagesByDate();

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <PageHeader
        title={`Chat History - ${user?.first_name || user?.username || 'User'}`}
        breadcrumbs={[
          { label: "Users", to: "/users" },
          { label: user?.first_name || user?.username || 'User', to: `/users/${id}` },
          { label: "Chat History" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/users/${id}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to User
          </Button>
        }
      />

      {/* Main chat container */}
      <div className="flex-grow flex flex-col bg-gray-50 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white p-3 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <span className="font-medium">Chat History</span>
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
              {user?.username || user?.email || 'User'}
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchMessages(id || '', 1)}
              title="Refresh conversation"
              disabled={isLoading}
            >
              <i className={`fas fa-sync-alt mr-1 ${isLoading ? 'animate-spin' : ''}`}></i>
              Refresh
            </Button>
            <Link to={`/users/${id}`}>
              <Button variant="ghost" size="sm" title="Back to user details">
                <i className="fas fa-arrow-left mr-1"></i> Back
              </Button>
            </Link>
          </div>
        </div>

        {/* Message area with scroll */}
        <div
          ref={messagesContainerRef}
          className="flex-grow overflow-y-auto p-4 space-y-4"
        >
          {isLoading && messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <i className="fas fa-comment-dots text-4xl mb-3"></i>
              <p>No messages found for this user.</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mx-auto max-w-lg">
                  {error}
                  <Button
                    variant="ghost"
                    size="xs"
                    className="ml-2"
                    onClick={() => setError(null)}
                  >
                    Close
                  </Button>
                </div>
              )}

              {/* Load more button */}
              {hasMore && (
                <div className="flex justify-center py-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-chevron-up mr-2"></i>
                        Load More
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Beginning of conversation marker */}
              {!hasMore && (
                <div className="text-center py-3 mb-2">
                  <span className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                    Beginning of conversation
                  </span>
                </div>
              )}

              {/* Messages - use the same format as ChatInterface */}
              {messageGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-6">
                  {/* Date separator */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                      {new Date(group.date).toLocaleDateString([], { 
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>

                  {/* Messages for this date */}
                  {group.messages.map((message, messageIndex) => {
                    const isUser = message.type === 'USER';
                    // const isAssistant = message.type === 'AI' || message.type === 'ASSISTANT'; // Não está sendo usado
                    const isSystem = message.type === 'SYSTEM';
                    // Format message content based on type
                    const content = formatMessageContent(message);

                    return (
                      <div
                        key={message.id || messageIndex}
                        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
                      >
                        <div
                          className={`max-w-3xl rounded-lg p-4 ${
                            isUser ? 'bg-blue-100 text-blue-900' :
                            isSystem ? 'bg-gray-100 text-gray-800 border border-gray-200' :
                            'bg-green-50 text-gray-800 border border-green-100'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="font-medium text-sm">
                              {isUser ? 'User' :
                               isSystem ? 'System' : 'Assistant'}
                            </div>
                            <div className="text-xs text-gray-500 ml-4">
                              {formatDate(message.created_at)}
                            </div>
                          </div>

                          <div className="prose prose-sm max-w-none">
                            <div className="whitespace-pre-wrap break-words">
                              <ReactMarkdown>{content}</ReactMarkdown>
                            </div>
                          </div>

                          {/* Tool executions (if any) */}
                          {(() => {
                            const toolExecutions = getToolExecutions(message);
                            return toolExecutions.length > 0 ? (
                              <div className="mt-3 pt-2 border-t border-gray-200 text-sm">
                                <div className="text-xs text-gray-500 font-medium mb-1">Tool Executions:</div>
                                {toolExecutions.map((tool: ChatToolExecution, idx: number) => (
                                  <div key={idx} className="text-xs mt-2 bg-gray-50 p-2 rounded border border-gray-200">
                                    <div className="font-medium text-gray-700">{tool.chatToolRequest?.name || 'Tool'}</div>
                                    <div className="mt-1 whitespace-pre-wrap text-gray-600 overflow-auto max-h-40" style={{ fontSize: '0.7rem' }}>
                                      {tool.result || 'No result'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Invisible element to scroll to end */}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Footer info bar */}
        {messages.length > 0 && (
          <div className="bg-white p-3 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
            <div>
              Messages: <span className="font-semibold">{messages.length}</span>
            </div>
            {user && (
              <div>
                User: <span className="font-semibold">{user?.username || user?.email || 'User'}</span>
              </div>
            )}
            <div className="flex items-center">
              <i className="fas fa-clock mr-1"></i>
              {messages.length > 0 && messages[messages.length - 1].created_at 
                ? new Date(messages[messages.length - 1].created_at as string).toLocaleString() 
                : 'Unknown date'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserChatHistory;